import { useEffect, useRef, useState } from 'react'

import { Hidden } from '@twistopayments/design-system/components/atoms/hidden'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'
import { ArrowIcon } from '@twistopayments/design-system/icons'

import type { SectionsImageBoxSliderComponent } from '@twisto/api-cms'

import { useHighlightedText } from 'homepage/libs/hooks'

import { ImageBox } from './image-box'
import * as styles from './image-box-slider.styles'

const SCROLL_OFFSET = 400

export const ImageBoxSlider = ({
  title,
  highlightedTitle,
  description,
  images,
}: SectionsImageBoxSliderComponent) => {
  const { after, before, highlight } = useHighlightedText(
    title,
    highlightedTitle
  )

  const sliderRef = useRef<HTMLDivElement>(null)

  const [showPrevButton, setShowPrevButton] = useState(false)
  const [showNextButton, setShowNextButton] = useState(false)

  const updateScrollButtons = () => {
    const slider = sliderRef.current

    if (!slider) {
      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = slider

    setShowPrevButton(scrollLeft > 0)
    setShowNextButton(scrollLeft + clientWidth < scrollWidth - 1)
  }

  useEffect(() => {
    updateScrollButtons()

    const slider = sliderRef.current

    if (!slider) {
      return
    }

    slider.addEventListener('scroll', updateScrollButtons)
    window.addEventListener('resize', updateScrollButtons)

    return () => {
      slider.removeEventListener('scroll', updateScrollButtons)
      window.removeEventListener('resize', updateScrollButtons)
    }
  }, [])

  const scrollSliderBy = (offset: number) => {
    sliderRef.current?.scrollBy({ left: offset, behavior: 'smooth' })
  }

  const scrollLeft = () => {
    scrollSliderBy(-SCROLL_OFFSET)
  }

  const scrollRight = () => {
    scrollSliderBy(SCROLL_OFFSET)
  }

  return (
    <section css={styles.section}>
      <Hidden smMdDown>
        {showPrevButton && (
          <button css={styles.sliderButton} onClick={scrollLeft}>
            <ArrowIcon />
          </button>
        )}
      </Hidden>
      <div css={styles.container}>
        <Typography css={styles.title} fontWeight="heavy" variant="h2">
          {before}
          <span css={styles.highlighted}>{highlight}</span>
          {after}
        </Typography>
        <p css={styles.description}>{description}</p>
      </div>
      <div css={styles.sliderWrapper}>
        <div ref={sliderRef} css={styles.slider}>
          {images?.map((image) => <ImageBox key={image.id} {...image} />)}
        </div>
      </div>
      <Hidden smMdDown>
        {showNextButton && (
          <button
            css={[styles.sliderButton, styles.next]}
            onClick={scrollRight}
          >
            <ArrowIcon />
          </button>
        )}
      </Hidden>
    </section>
  )
}
